import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Radio, 
  Checkbox, 
  message, 
  Steps, 
  Modal,
  Row,
  Col,
  Statistic,
  Progress,
  Alert,
  Result
} from 'antd';
import { UserOutlined, BookOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const { Step } = Steps;
const { TextArea } = Input;
const { Countdown } = Statistic;

interface Test {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  start_time?: string;
  end_time?: string;
  time_limit?: number;
}

interface Question {
  id: string;
  question_text: string;
  question_type: string;
  options: string[];
  correct_answer: string;
  points: number;
  knowledge_points: string;
}

interface StudentInfo {
  name: string;
  email: string;
  student_id: string;
  class_name: string;
}

const StudentTest: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [test, setTest] = useState<Test | null>(null);
  const [studentInfo, setStudentInfo] = useState<StudentInfo | null>(null);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [documentPreview, setDocumentPreview] = useState<any>(null);
  const [form] = Form.useForm();
  
  const [timeStatus, setTimeStatus] = useState<{
    status: string;
    message: string;
    time_limit?: number;
    end_time?: string;
  } | null>(null);
  const [examStartTime, setExamStartTime] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const autoSubmitRef = useRef<boolean>(false);

  const handleAutoSubmit = useCallback(async () => {
    if (autoSubmitRef.current || testCompleted) return;
    autoSubmitRef.current = true;
    setIsTimeUp(true);
    message.warning('Time is up! Your answers are being submitted automatically.');
    
    if (studentInfo && test) {
      try {
        const studentResponse = await axios.post('/api/students', studentInfo);
        const studentId = studentResponse.data.studentId;
        const answerData = Object.entries(answers).map(([questionId, answer]) => ({
          question_id: questionId,
          answer_text: answer
        }));
        const result = await axios.post('/api/answers', {
          test_id: testId,
          student_id: studentId,
          answers: answerData
        });
        setTestResult(result.data);
        setTestCompleted(true);
        setCurrentStep(2);
      } catch (error) {
        message.error('Failed to auto-submit answers');
      }
    }
  }, [studentInfo, test, answers, testId, testCompleted]);

  useEffect(() => {
    if (examStartTime && timeStatus?.time_limit && currentStep === 1 && !testCompleted) {
      const endTime = examStartTime + (timeStatus.time_limit * 60 * 1000);
      
      const timer = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, endTime - now);
        setTimeRemaining(remaining);
        
        if (remaining <= 0 && !autoSubmitRef.current) {
          clearInterval(timer);
          handleAutoSubmit();
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [examStartTime, timeStatus, currentStep, testCompleted, handleAutoSubmit]);

  const fetchTest = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/tests/${testId}`);
      setTest(response.data);
    } catch (error) {
      message.error('Failed to fetch test information');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeStatus = async () => {
    try {
      const response = await axios.get(`/api/tests/${testId}/time-status`);
      setTimeStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch time status');
    }
  };

  const fetchDocumentPreview = async () => {
    try {
      const response = await axios.get(`/api/preview/${testId}`);
      console.log('Document preview response:', response.data);
      setDocumentPreview(response.data);
    } catch (error) {
      console.error('Failed to fetch document preview:', error);
    }
  };

  useEffect(() => {
    if (testId) {
      fetchTest();
      fetchDocumentPreview();
      fetchTimeStatus();
    }
  }, [testId]);

  const handleSubmitStudentInfo = (values: StudentInfo) => {
    if (timeStatus?.status === 'not_started') {
      message.error(timeStatus.message);
      return;
    }
    if (timeStatus?.status === 'ended') {
      message.error(timeStatus.message);
      return;
    }
    
    setStudentInfo(values);
    setCurrentStep(1);
    setExamStartTime(Date.now());
    message.success('Personal information submitted successfully');
  };

  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Submit answers
  const handleSubmitAnswers = async () => {
    if (!studentInfo || !test) return;

    setLoading(true);
    try {
      // Register student first
      const studentResponse = await axios.post('/api/students', studentInfo);
      const studentId = studentResponse.data.studentId;

      // Prepare answer data
      const answerData = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer_text: answer
      }));

      // Submit answers
      const result = await axios.post('/api/answers', {
        test_id: testId,
        student_id: studentId,
        answers: answerData
      });

      setTestResult(result.data);
      setTestCompleted(true);
      setCurrentStep(2);
      message.success('Test submitted successfully!');
    } catch (error) {
      message.error('Failed to submit answers');
    } finally {
      setLoading(false);
    }
  };

  // Handle answer change
  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Render question
  const renderQuestion = (question: Question, index: number) => {
    const { id, question_text, question_type, options, points } = question;

    return (
      <Card key={id} size="small" style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '12px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Question {index + 1} ({points} points)</h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>{question_text}</p>
        </div>

        {question_type === 'multiple_choice' && (
          <Radio.Group
            value={answers[id]}
            onChange={(e) => handleAnswerChange(id, e.target.value)}
            style={{ width: '100%' }}
          >
            {(() => {
              try {
                let parsedOptions = options;
                if (typeof options === 'string') {
                  parsedOptions = JSON.parse(options);
                  if (typeof parsedOptions === 'string') {
                    parsedOptions = JSON.parse(parsedOptions);
                  }
                }
                return Array.isArray(parsedOptions) ? parsedOptions : [];
              } catch (e) {
                return [];
              }
            })().map((option: string, optIndex: number) => (
              <Radio 
                key={optIndex} 
                value={option}
                style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}
              >
                {String.fromCharCode(65 + optIndex)}. {option}
              </Radio>
            ))}
          </Radio.Group>
        )}

        {question_type === 'true_false' && (
          <Radio.Group
            value={answers[id]}
            onChange={(e) => handleAnswerChange(id, e.target.value)}
            style={{ width: '100%' }}
          >
            <Radio value="true" style={{ 
              display: 'block', 
              marginBottom: '6px',
              fontSize: '14px'
            }}>
              True
            </Radio>
            <Radio value="false" style={{ 
              display: 'block', 
              marginBottom: '6px',
              fontSize: '14px'
            }}>
              False
            </Radio>
          </Radio.Group>
        )}

        {question_type === 'fill_blank' && (
          <Input
            value={answers[id] || ''}
            onChange={(e) => handleAnswerChange(id, e.target.value)}
            placeholder="Enter your answer"
            style={{ width: '100%', maxWidth: '300px' }}
          />
        )}
      </Card>
    );
  };

  // Calculate answer progress
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = test?.questions?.length || 0;
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  if (loading && !test) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Test does not exist or has been deleted</div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{test?.title ? `${test.title} – Online Test` : 'Online Test – Smart Test Statistics System'}</title>
        <meta name="description" content={test?.description || `Take the online test: ${test?.title || ''}. Enter your information and submit your answers for instant grading.`} />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={test?.title ? `${test.title} – Online Test` : 'Online Test'} />
        <meta property="og:description" content={test?.description || `Take the online test: ${test?.title || ''}.`} />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="header">
        <h1>Online Test</h1>
        <p>{test.title}</p>
      </div>

      <div className="container">
        <Steps current={currentStep} style={{ marginBottom: '30px' }}>
          <Step title="Personal Info" icon={<UserOutlined />} />
          <Step title="Answer Questions" icon={<BookOutlined />} />
          <Step title="Complete" icon={<CheckCircleOutlined />} />
        </Steps>

        {currentStep === 0 && (
          <Card title="Fill in Personal Information" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmitStudentInfo}
            >
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input placeholder="Enter your name" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input placeholder="Enter your email" />
              </Form.Item>

              <Form.Item
                name="student_id"
                label="Student ID"
                rules={[{ required: true, message: 'Please enter your student ID' }]}
              >
                <Input placeholder="Enter your student ID" />
              </Form.Item>

              <Form.Item
                name="class_name"
                label="Class"
                rules={[{ required: true, message: 'Please enter your class' }]}
              >
                <Input placeholder="Enter your class" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block>
                  Start Test
                </Button>
              </Form.Item>
            </Form>
          </Card>
        )}

        {currentStep === 1 && (
          <div>
            {timeStatus?.time_limit && timeRemaining !== null && (
              <Alert
                message={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                      <ClockCircleOutlined style={{ marginRight: 8 }} />
                      Time Remaining
                    </span>
                    <span style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold',
                      color: timeRemaining < 60000 ? '#ff4d4f' : timeRemaining < 300000 ? '#faad14' : '#52c41a'
                    }}>
                      {formatTime(timeRemaining)}
                    </span>
                  </div>
                }
                type={timeRemaining < 60000 ? 'error' : timeRemaining < 300000 ? 'warning' : 'info'}
                style={{ marginBottom: 20 }}
                showIcon={false}
              />
            )}
            
            <Card style={{ marginBottom: '20px' }}>
              <Row gutter={16}>
                <Col span={timeStatus?.time_limit ? 8 : 12}>
                  <Statistic title="Total Questions" value={totalQuestions} />
                </Col>
                <Col span={timeStatus?.time_limit ? 8 : 12}>
                  <Statistic title="Answered" value={answeredCount} />
                </Col>
                {timeStatus?.time_limit && (
                  <Col span={8}>
                    <Statistic title="Time Limit" value={timeStatus.time_limit} suffix="min" />
                  </Col>
                )}
              </Row>
              <Progress 
                percent={Math.round(progress)} 
                status={progress === 100 ? 'success' : 'active'}
                style={{ marginTop: '10px' }}
              />
            </Card>

            {/* Left-right layout */}
            <Row gutter={24}>
              {/* Left: Original document */}
              <Col span={12}>
                <Card 
                  title="📄 Original Document" 
                  style={{ 
                    position: 'sticky',
                    top: '20px',
                    alignSelf: 'flex-start'
                  }}
                >
                  {documentPreview ? (
                    <div style={{ maxHeight: 'calc(100vh - 150px)', overflow: 'auto' }}>
                      {documentPreview.fileType === 'word' && (
                        <div 
                          style={{ 
                            padding: '20px',
                            backgroundColor: '#fafafa',
                            borderRadius: '4px',
                            minHeight: '100%'
                          }}
                          dangerouslySetInnerHTML={{ __html: documentPreview.content }}
                        />
                      )}
                      {documentPreview.fileType === 'pdf' && documentPreview.filePath && (
                        <div style={{ width: '100%' }}>
                          <iframe 
                            src={`/${(documentPreview.filePath.replace(/\\/g, '/').split('/').map((segment: string) => encodeURIComponent(segment)).join('/'))}`}
                            width="100%" 
                            height="900px"
                            style={{ border: 'none' }}
                            title="PDF Document Preview"
                            onError={(e) => {
                              console.error('PDF load error:', e);
                            }}
                          />
                        </div>
                      )}
                      {documentPreview.fileType === 'image' && documentPreview.filePath && (
                        <div style={{ 
                          textAlign: 'center', 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          justifyContent: 'center',
                          backgroundColor: '#fafafa',
                          padding: '20px',
                          minHeight: '400px'
                        }}>
                          <img 
                            src={`/${(documentPreview.filePath.replace(/\\/g, '/').split('/').map((segment: string) => encodeURIComponent(segment)).join('/'))}`}
                            alt="Original document image"
                            style={{ 
                              maxWidth: '100%', 
                              height: 'auto',
                              objectFit: 'contain',
                              display: 'block'
                            }}
                            onError={(e) => {
                              console.error('Image load error:', e);
                              console.error('Image src:', (e.target as HTMLImageElement).src);
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent && !parent.querySelector('.error-message')) {
                                const errorDiv = document.createElement('div');
                                errorDiv.className = 'error-message';
                                errorDiv.textContent = `图片加载失败: ${target.src}`;
                                errorDiv.style.color = '#ff4d4f';
                                errorDiv.style.padding = '20px';
                                parent.appendChild(errorDiv);
                              }
                            }}
                            onLoad={() => {
                              console.log('Image loaded successfully:', documentPreview.filePath);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      📄 No document preview available
                    </div>
                  )}
                </Card>
              </Col>

              {/* Right: Answer area */}
              <Col span={12}>
                <div>
                  {test.questions && test.questions.length > 0 ? (
                    <div>
                      {test.questions.map((question, index) => 
                        renderQuestion(question, index)
                      )}
                      
                      <Card style={{ textAlign: 'center', marginTop: '20px' }}>
                        <Button 
                          type="primary" 
                          size="large"
                          onClick={handleSubmitAnswers}
                          loading={loading}
                          disabled={answeredCount < totalQuestions}
                        >
                          Submit Answers
                        </Button>
                        {answeredCount < totalQuestions && totalQuestions > 0 && (
                          <div style={{ marginTop: '10px', color: '#ff4d4f' }}>
                            Please complete all questions before submitting
                          </div>
                        )}
                      </Card>
                    </div>
                  ) : (
                    <Card style={{ textAlign: 'center', padding: '40px' }}>
                      <div style={{ color: '#999', fontSize: '16px' }}>
                        📝 No questions available. Please wait for the teacher to add questions.
                      </div>
                      <div style={{ marginTop: '10px', color: '#666' }}>
                        You can view the original document on the left
                      </div>
                    </Card>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        )}

        {currentStep === 2 && testResult && (
          <Card title="Test Results" style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '30px' }}>
              <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a' }} />
            </div>
            
            <Row gutter={16} style={{ marginBottom: '30px' }}>
              <Col span={8}>
                <Statistic 
                  title="Score" 
                  value={testResult.totalScore} 
                  suffix={`/ ${testResult.maxScore}`}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Accuracy" 
                  value={testResult.percentage} 
                  suffix="%"
                  precision={1}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Grade" 
                  value={
                    testResult.percentage >= 90 ? 'Excellent' :
                    testResult.percentage >= 80 ? 'Good' :
                    testResult.percentage >= 70 ? 'Average' :
                    testResult.percentage >= 60 ? 'Pass' : 'Fail'
                  }
                />
              </Col>
            </Row>

            <Button 
              type="primary" 
              size="large"
              onClick={() => window.location.href = '/'}
            >
              Return to Home
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StudentTest;
